import {
  Modal,
  Box,
  IconButton,
  CircularProgress,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect } from "react";
import { convertHeicToJpeg } from "@components/photo-upload";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import { FileWithPreview } from "@contexts/index";

function PhotoModal({
  open,
  handleClose,
  photo,
}: {
  open: boolean;
  handleClose: () => void;
  photo: FileWithPreview;
}) {
  const [imageUrl, setImageUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const translate = useAppTranslate();

  useEffect(() => {
    if (!photo || !open) return;

    const loadImage = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        // If it's a HEIC/HEIF file and we don't have a converted preview yet
        if (photo.isHeic && !photo.convertedPreview) {
          const convertedUrl = await convertHeicToJpeg(photo.file);
          setImageUrl(convertedUrl);

          // Optionally cache the converted URL back to the photo object
          // This would require updating the files context
          photo.convertedPreview = convertedUrl;
        } else if (photo.convertedPreview) {
          // Use cached converted preview
          setImageUrl(photo.convertedPreview);
        } else {
          // Use regular preview for non-HEIC files
          setImageUrl(photo.preview);
        }
      } catch (error) {
        console.error("Failed to load/convert image:", error);
        setHasError(true);
        // Fallback to preview
        setImageUrl(photo.preview);
      } finally {
        setIsLoading(false);
      }
    };

    loadImage();
  }, [photo, open]);

  if (!photo) {
    return null;
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        onClick={handleClose}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            width: { xs: "90%", sm: "70%", md: "50%" },
            maxHeight: "90vh",
            overflow: "auto",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            backgroundColor: "white",
            borderRadius: "8px",
          }}
        >
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>

          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "200px",
                padding: "40px",
              }}
            >
              <CircularProgress size={60} sx={{ marginBottom: "16px" }} />
              <Typography variant="h6" color="text.secondary">
                {translate(
                  "photoModal.converting",
                  "Converting HEIC/HEIF photo..."
                )}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginTop: "8px" }}
              >
                {translate(
                  "photoModal.pleaseWait",
                  "Please wait, this may take a moment."
                )}
              </Typography>
            </Box>
          ) : hasError ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "200px",
                padding: "40px",
              }}
            >
              <Typography variant="h6" color="error">
                {translate("photoModal.error", "Failed to load image")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginTop: "8px" }}
              >
                {translate(
                  "photoModal.errorDescription",
                  "There was an error converting or loading this photo."
                )}
              </Typography>
            </Box>
          ) : (
            <img
              src={imageUrl}
              alt={photo.file.name}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                borderRadius: "8px",
              }}
              onError={() => setHasError(true)}
            />
          )}
        </Box>
      </Box>
    </Modal>
  );
}

export default PhotoModal;
